import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, Select, message, Popconfirm, Tag, DatePicker } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { getUserRolePageList, getUserRoleDetail, addUserRole, updateUserRole, updateUserRoleStatus } from '../../api/userRole';
import './index.css';
import dayjs from 'dayjs';

// antd Select.Option 在部分 TS 配置下类型较严格，这里用 any 避免影响页面逻辑
const Option: any = (Select as any).Option;

const UserRoles = () => {
  const [userRoles, setUserRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUserRole, setEditingUserRole] = useState(null);
  const [searchParams, setSearchParams] = useState({
    userId: null,
    tenantId: null,
    roleId: null,
    grantType: null,
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });
  const [form] = Form.useForm();

  // 表格列定义
  const columns: any[] = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '用户ID',
      dataIndex: 'userId',
      key: 'userId',
      width: 100,
    },
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      width: 140,
      render: (text) => text || '-',
    },
    {
      title: '租户ID',
      dataIndex: 'tenantId',
      key: 'tenantId',
      width: 100,
    },
    {
      title: '租户名称',
      dataIndex: 'tenantName',
      key: 'tenantName',
      width: 160,
      render: (text) => text || '-',
    },
    {
      title: '角色ID',
      dataIndex: 'roleId',
      key: 'roleId',
      width: 100,
    },
    {
      title: '角色名称',
      dataIndex: 'roleName',
      key: 'roleName',
      width: 160,
      render: (text) => text || '-',
    },
    {
      title: '授权类型',
      dataIndex: 'grantType',
      key: 'grantType',
      width: 120,
      render: (type) => {
        const typeMap = {
          0: '直接授权',
          1: '继承授权',
        };
        return typeMap[type] || '未知';
      },
    },
    {
      title: '生效时间',
      dataIndex: 'effectiveTime',
      key: 'effectiveTime',
      width: 180,
      render: (text) => {
        if (!text) return '-';
        return dayjs(text).format('YYYY-MM-DD HH:mm:ss');
      },
    },
    {
      title: '过期时间',
      dataIndex: 'expireTime',
      key: 'expireTime',
      width: 180,
      render: (text) => {
        if (!text) return '-';
        return dayjs(text).format('YYYY-MM-DD HH:mm:ss');
      },
    },
    {
      title: '状态',
      dataIndex: 'isEnabled',
      key: 'isEnabled',
      width: 100,
      render: (isEnabled) => (
        <Tag color={isEnabled === 1 ? 'green' : 'red'}>
          {isEnabled === 1 ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'create_time',
      key: 'create_time',
      width: 180,
      render: (text) => {
        if (!text) return '-';
        return dayjs(text).format('YYYY-MM-DD HH:mm:ss');
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Button
            type="link"
            onClick={() => handleToggleStatus(record)}
          >
            {record.isEnabled === 1 ? '禁用' : '启用'}
          </Button>
        </Space>
      ),
    },
  ];

  // 获取用户角色列表
  const fetchUserRoles = async () => {
    setLoading(true);
    try {
      const params = {
        PageIndex: pagination.current,
        PageSize: pagination.pageSize,
        ...searchParams,
      };
      
      const response: any = await getUserRolePageList(params);
      
      if (response?.res_status_code === '0' && response?.res_content) {
        setUserRoles(response.res_content.items || []);
        const rawTotal =
          response?.res_content?.totalCount ??
          response?.res_content?.TotalCount ??
          response?.totalCount ??
          response?.TotalCount ??
          response?.res_content?.total ??
          response?.res_content?.Total ??
          0;
        const total = Number(rawTotal) || 0;
        setPagination((prev) => ({ ...prev, total }));
      } else {
        message.error(response?.res_message || '获取用户角色列表失败');
      }
    } catch (error) {
      console.error('获取用户角色列表失败:', error);
      message.error('获取用户角色列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserRoles();
  }, [pagination.current, pagination.pageSize]);

  // 搜索
  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 });
    fetchUserRoles();
  };

  // 重置搜索
  const handleReset = () => {
    setSearchParams({
      userId: null,
      tenantId: null,
      roleId: null,
      grantType: null,
    });
    setPagination({ ...pagination, current: 1 });
    setTimeout(() => {
      fetchUserRoles();
    }, 100);
  };

  // 新增用户角色
  const handleAdd = () => {
    setEditingUserRole(null);
    form.resetFields();
    setModalVisible(true);
  };

  // 编辑用户角色
  const handleEdit = async (record) => {
    try {
      const response: any = await getUserRoleDetail(record.id);
      if (response?.res_status_code === '0' && response?.res_content) {
        const userRole = response.res_content;
        setEditingUserRole(userRole);
        form.setFieldsValue({
          ...userRole,
          effectiveTime: userRole.effectiveTime ? dayjs(userRole.effectiveTime) : null,
          expireTime: userRole.expireTime ? dayjs(userRole.expireTime) : null,
        });
        setModalVisible(true);
      } else {
        message.error(response?.res_message || '获取用户角色详情失败');
      }
    } catch (error) {
      console.error('获取用户角色详情失败:', error);
      message.error('获取用户角色详情失败');
    }
  };

  // 保存用户角色
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const data = {
        ...values,
        effectiveTime: values.effectiveTime ? values.effectiveTime.format('YYYY-MM-DD HH:mm:ss') : null,
        expireTime: values.expireTime ? values.expireTime.format('YYYY-MM-DD HH:mm:ss') : null,
      };

      if (editingUserRole) {
        // 更新
        data.Id = editingUserRole.id;
        const response: any = await updateUserRole(data);
        if (response?.res_status_code === '0') {
          message.success('更新用户角色成功');
          setModalVisible(false);
          fetchUserRoles();
        } else {
          message.error(response?.res_message || '更新用户角色失败');
        }
      } else {
        // 新增
        const response: any = await addUserRole(data);
        if (response?.res_status_code === '0') {
          message.success('添加用户角色成功');
          setModalVisible(false);
          fetchUserRoles();
        } else {
          message.error(response?.res_message || '添加用户角色失败');
        }
      }
    } catch (error) {
      console.error('保存用户角色失败:', error);
      if (error.errorFields) {
        return; // 表单验证错误
      }
      message.error('保存用户角色失败');
    }
  };

  // 切换状态
  const handleToggleStatus = async (record) => {
    try {
      const newStatus = record.isEnabled === 1 ? 0 : 1;
      const response: any = await updateUserRoleStatus({
        IsEnabled: newStatus,
        Ids: [record.id],
      });
      if (response?.res_status_code === '0') {
        message.success(`${newStatus === 1 ? '启用' : '禁用'}用户角色成功`);
        fetchUserRoles();
      } else {
        message.error(response?.res_message || '更新状态失败');
      }
    } catch (error) {
      console.error('更新状态失败:', error);
      message.error('更新状态失败');
    }
  };

  // 分页变化
  const handleTableChange = (newPagination) => {
    setPagination((prev) => ({
      ...prev,
      current:
        newPagination.pageSize && newPagination.pageSize !== prev.pageSize
          ? 1
          : newPagination.current ?? prev.current,
      pageSize: newPagination.pageSize ?? prev.pageSize,
    }));
  };

  return (
    <div className="user-roles-page">
      <div className="page-header">
        <h2>用户角色管理</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          新增用户角色
        </Button>
      </div>

      {/* 搜索区域 */}
      <div className="search-container">
        <Space size="middle" wrap>
          <Input
            placeholder="用户ID"
            value={searchParams.userId}
            onChange={(e) => setSearchParams({ ...searchParams, userId: e.target.value ? parseInt(e.target.value) : null })}
            style={{ width: 150 }}
            allowClear
          />
          <Input
            placeholder="租户ID"
            value={searchParams.tenantId}
            onChange={(e) => setSearchParams({ ...searchParams, tenantId: e.target.value ? parseInt(e.target.value) : null })}
            style={{ width: 150 }}
            allowClear
          />
          <Input
            placeholder="角色ID"
            value={searchParams.roleId}
            onChange={(e) => setSearchParams({ ...searchParams, roleId: e.target.value ? parseInt(e.target.value) : null })}
            style={{ width: 150 }}
            allowClear
          />
          <Select
            placeholder="授权类型"
            value={searchParams.grantType}
            onChange={(value) => setSearchParams({ ...searchParams, grantType: value })}
            style={{ width: 150 }}
            allowClear
          >
            <Option value={0}>直接授权</Option>
            <Option value={1}>继承授权</Option>
          </Select>
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
            搜索
          </Button>
          <Button onClick={handleReset}>重置</Button>
        </Space>
      </div>

      {/* 表格 */}
      <Table
        columns={columns}
        dataSource={userRoles}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条`,
          size: 'small',
        }}
        onChange={handleTableChange}
        scroll={{ x: 1600, y: 'calc(100vh - 320px)' }}
      />

      {/* 新增/编辑弹窗 */}
      <Modal
        title={editingUserRole ? '编辑用户角色' : '新增用户角色'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        width={600}
        destroyOnClose
      >
        <Form
          form={form}
          layout="horizontal"
          labelAlign="right"
          colon={false}
          labelCol={{ flex: '110px' }}
          wrapperCol={{ flex: 1 }}
        >
          <Form.Item
            name="userId"
            label="用户ID"
            rules={[{ required: true, message: '请输入用户ID' }]}
          >
            <Input type="number" placeholder="请输入用户ID" />
          </Form.Item>
          <Form.Item
            name="username"
            label="用户名"
          >
            <Input placeholder="请输入用户名" />
          </Form.Item>
          <Form.Item
            name="tenantId"
            label="租户ID"
            rules={[{ required: true, message: '请输入租户ID' }]}
          >
            <Input type="number" placeholder="请输入租户ID" />
          </Form.Item>
          <Form.Item
            name="tenantName"
            label="租户名称"
          >
            <Input placeholder="请输入租户名称" />
          </Form.Item>
          <Form.Item
            name="roleId"
            label="角色ID"
            rules={[{ required: true, message: '请输入角色ID' }]}
          >
            <Input type="number" placeholder="请输入角色ID" />
          </Form.Item>
          <Form.Item
            name="roleName"
            label="角色名称"
          >
            <Input placeholder="请输入角色名称" />
          </Form.Item>
          <Form.Item
            name="grantType"
            label="授权类型"
          >
            <Select>
              <Option value={0}>直接授权</Option>
              <Option value={1}>继承授权</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="effectiveTime"
            label="生效时间"
          >
            <DatePicker
              showTime
              style={{ width: '100%' }}
              format="YYYY-MM-DD HH:mm:ss"
            />
          </Form.Item>
          <Form.Item
            name="expireTime"
            label="过期时间"
          >
            <DatePicker
              showTime
              style={{ width: '100%' }}
              format="YYYY-MM-DD HH:mm:ss"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserRoles;
